import React, { Fragment } from "react";
import { useSelector, useDispatch } from "react-redux";
import PropTypes from "prop-types";
import classnames from "classnames";
import {
  getPriceTagging,
  formatMoney,
  getIndividualPriceTagging,
  isPlanMemberGrouped,
  getMemberGroupByType,
  isPlanRequireUW,
  isGroupPlan,
} from "./EnrollmentDataUtil";
import messages from "components/messagesDescription";
import { FormattedMessage, FormattedHTMLMessage } from "react-intl";
import UwIcon from "../../../assets/images/svg/uw_icon.svg";
import { changePlan } from "../../../actions/enrollmentActions";
import { SUM_ASSURED_BASED } from "./constants";

const MemberGroupPrice = ({ members, plan, priceTagMode }) => {
  const pricing = getPriceTagging(priceTagMode, members, plan);
  const priceMode = !pricing.isRelativePrice
    ? "absolute"
    : pricing.relativeTagNode === "Standard"
    ? "standard"
    : "relative";

  return (
    <div>
      {priceMode === "absolute" ? (
        <div>
          <FormattedMessage {...messages.enrollment.spendDescription} />
          &nbsp;
          <strong>{formatMoney(pricing.price)}</strong>
        </div>
      ) : priceMode === "relative" ? (
        <div>
          <FormattedMessage {...messages.relativeTagNode} />
          &nbsp;
          <strong>{formatMoney(pricing.relativePrice)}</strong>
        </div>
      ) : (
        <FormattedMessage {...messages.standardDescription} />
      )}
    </div>
  );
};

MemberGroupPrice.propTypes = {
  plan: PropTypes.object.isRequired,
  members: PropTypes.array,
  priceTagMode: PropTypes.string,
};

const PlanBoxMini = ({ benefit, plan, intl }) => {
  const dispatch = useDispatch();
  const priceTagMode = useSelector(state => state.enrollment.details.priceTagMode);
  const showIntervalOptions =
    Array.isArray(plan.intervalOptions) && plan.intervalOptions.length > 0;
  const componentCls = classnames("product-plan", "product-plan--selectable", {
    "product-plan--active button--tiles--active": plan.isSelected,
  });
  const isMemberGroup = isPlanMemberGrouped(plan);
  const isGroupedPlan = isGroupPlan(plan);
  let planName = plan.name;
  if (!planName && isGroupedPlan) {
    const selectedPlan = plan.plans.find(p => p.isSelected);
    if (selectedPlan) {
      planName = selectedPlan.planName;
    } else {
      planName = plan.plans[0].planName;
    }
  }

  let description = plan.descriptionLine1;
  if (!description && isMemberGroup) {
    const selectedPlan = plan.plans.find(p => p.isSelected);
    if (selectedPlan) {
      description = selectedPlan.description;
    } else {
      description = plan.plans[0].description;
    }
  }

  let subDescription = plan.descriptionLine2;
  if (!description && isMemberGroup) {
    const selectedPlan = plan.plans.find(p => p.isSelected);
    if (selectedPlan) {
      description = selectedPlan.groupName;
    } else {
      description = plan.plans[0].groupName;
    }
  }

  const PlanName = () => (
    <div style={{ marginBottom: "10px" }}>
      <strong>
        {!isMemberGroup &&
          planName &&
          intl.formatMessage({
            id: planName,
            defaultMessage: planName,
          })}
        {isMemberGroup &&
          description &&
          intl
            .formatMessage({
              id: description,
              defaultMessage: description,
            })
            .concat(" ")}
        {isMemberGroup &&
          subDescription &&
          intl.formatMessage({
            id: subDescription,
            defaultMessage: subDescription,
          })}
      </strong>
    </div>
  );
  const PlanValue = () => {
    const planValue = plan.planValue && plan.planValue.numericValue;
    return planValue ? (
      <div style={{ marginBottom: "10px" }}>
        <span className="product-plan__description">
          {benefit.type === SUM_ASSURED_BASED && "Sum Assured"}
          <strong>{formatMoney(planValue)}</strong>
        </span>
      </div>
    ) : (
      ""
    );
  };
  const getPlanPrice = plan => {
    const eligibleMembers =
      plan.eligibleMembers || (plan.plans[0] && plan.plans[0].eligibleMembers);
    return eligibleMembers.selectionMode === "Individual"
      ? getIndividualPriceTagging(
          priceTagMode,
          eligibleMembers.members,
          benefit.benefitOptions.planType
        )
      : getPriceTagging(priceTagMode, eligibleMembers.members, plan);
  };

  const PlanPrice = () => {
    const pricing = getPlanPrice(plan);
    const priceMode = !pricing.isRelativePrice
      ? "absolute"
      : pricing.relativeTagNode === "Standard"
      ? "standard"
      : "relative";

    return (
      <div style={{ marginBottom: "10px" }}>
        {priceMode === "absolute" ? (
          <div>
            {pricing.price != 0 &&
              (pricing.price > 0 ? (
                <FormattedMessage {...messages.enrollment.spendDescription} />
              ) : (
                <FormattedMessage {...messages.enrollment.creditDescription} />
              ))}
            &nbsp;
            <strong>{formatMoney(Math.abs(pricing.price))}</strong>
          </div>
        ) : priceMode === "relative" ? (
          <div>
            {pricing.price != 0 &&
              (pricing.relativePrice > 0 ? (
                <FormattedMessage {...messages.enrollment.spendDescription} />
              ) : (
                <FormattedMessage {...messages.enrollment.creditDescription} />
              ))}
            &nbsp;
            <strong>{formatMoney(Math.abs(pricing.relativePrice))}</strong>
          </div>
        ) : (
          <FormattedMessage {...messages.standardDescription} />
        )}
      </div>
    );
  };

  const IntervalPlanPrice = () => {
    const price = plan.pricing.price;
    return (
      <div style={{ marginBottom: "10px" }}>
        <FormattedMessage {...messages.enrollment.spendDescription} />
        &nbsp;
        <strong>{formatMoney(price.numericValue || 0)}</strong>
      </div>
    );
  };

  const HDFNote = () => {
    const isHDFRequired = isPlanRequireUW(plan);
    return (
      <span className="button--tiles__note">
        {isHDFRequired && (
          <Fragment>
            <span className="product-plan__hdfDescription">
              <FormattedHTMLMessage {...messages.HDFRDescription} />
            </span>
            <span className="product-plan__hdfIcon">
              <UwIcon />
            </span>
          </Fragment>
        )}
      </span>
    );
  };

  const MemberGroupDetail = () => {
    const option = plan.plans.find(p => p.isSelected);
    return (
      <div className="product-plan__details">
        <div style={{ marginBottom: "10px" }}>
          <strong>{getMemberGroupByType(option.priceLabelName, option.memberType, intl)}</strong>
        </div>
        <MemberGroupPrice
          members={option.eligibleMembers.members}
          plan={plan}
          priceTagMode={priceTagMode}
        />
      </div>
    );
  };
  return (
    <div className="grid__item grid__item--six product-panel__plan">
      <div className="product-plan-wrapper">
        <button
          type="button"
          style={{ textAlign: "left" }}
          onClick={() => dispatch(changePlan(benefit, plan))}
          className={componentCls}
        >
          <div className="product-plan__details">
            {showIntervalOptions ? (
              <Fragment>
                <PlanValue />
                <IntervalPlanPrice />
              </Fragment>
            ) : (
              <Fragment>
                <PlanName />
                {plan.name !== "No Coverage" && <PlanValue />}
                {!isMemberGroup ? <PlanPrice /> : <MemberGroupDetail />}
              </Fragment>
            )}
            <HDFNote />
          </div>
        </button>
      </div>
    </div>
  );
};

PlanBoxMini.propTypes = {
  benefit: PropTypes.object.isRequired,
  plan: PropTypes.object.isRequired,
  isNarrowScreen: PropTypes.bool,
  intl: PropTypes.func,
};
export default PlanBoxMini;



// WEBPACK FOOTER //
// ./components/employee/shop/PlanBoxMini.js